import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Scale, 
  ClipboardCheck, 
  Send, 
  Users, 
  TrendingUp,
  Loader2,
  ChevronDown,
  ChevronUp,
  Thermometer
} from 'lucide-react';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { apiRequest } from '../lib/utils';
import { cn } from '../lib/utils';

const iconMap = {
  FileText: FileText,
  Scale: Scale,
  ClipboardCheck: ClipboardCheck,
  Send: Send,
  Users: Users,
};

const colorMap = {
  emerald: {
    bg: 'bg-emerald-500',
    bgLight: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-700 dark:text-emerald-400',
    border: 'border-emerald-200 dark:border-emerald-800'
  },
  amber: {
    bg: 'bg-amber-500',
    bgLight: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800'
  },
  orange: {
    bg: 'bg-orange-500',
    bgLight: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-400',
    border: 'border-orange-200 dark:border-orange-800'
  },
  red: {
    bg: 'bg-red-500',
    bgLight: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-400',
    border: 'border-red-200 dark:border-red-800'
  }
};

export const UsageThermometer = ({ farmaciaId, compact = false }) => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (farmaciaId) {
      fetchUsage();
    }
  }, [farmaciaId]);

  const fetchUsage = async () => {
    try {
      setLoading(true);
      const data = await apiRequest(`/trial/usage/${farmaciaId}`);
      setUsage(data);
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar uso:', err);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-2">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !usage) {
    return (
      <div className="text-xs text-slate-400">-</div>
    );
  }

  const { summary, categories } = usage;
  const colors = colorMap[summary.engagement_color] || colorMap.amber;

  // Versão compacta para a tabela
  if (compact) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden w-20">
            <div 
              className={cn("h-full transition-all duration-500", colors.bg)}
              style={{ width: `${summary.overall_percentage}%` }}
            />
          </div>
          <span className={cn("text-xs font-medium", colors.text)}>
            {summary.overall_percentage}%
          </span>
        </div>
        <span className="text-[10px] text-slate-500">
          {summary.features_explored}/{summary.total_features} funções
        </span>
      </div>
    );
  }

  // Versão expandida
  return (
    <div className={cn(
      "rounded-lg border p-3 transition-all",
      colors.bgLight,
      colors.border
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Thermometer className={cn("w-4 h-4", colors.text)} />
          <span className={cn("text-sm font-medium", colors.text)}>
            {summary.engagement_label}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className="h-6 w-6 p-0"
        >
          {expanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      </div>

      {/* Barra principal */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-3 bg-white/50 dark:bg-slate-800/50 rounded-full overflow-hidden">
          <div 
            className={cn("h-full transition-all duration-500", colors.bg)}
            style={{ width: `${summary.overall_percentage}%` }}
          />
        </div>
        <span className={cn("text-lg font-bold", colors.text)}>
          {summary.overall_percentage}%
        </span>
      </div>

      {/* Resumo */}
      <div className="flex justify-between mt-2 text-xs text-slate-600 dark:text-slate-400">
        <span>{summary.total_items_used}/{summary.total_items_limit} itens criados</span>
        <span>{summary.features_explored}/{summary.total_features} funcionalidades</span>
      </div>

      {/* Detalhes expandidos */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-200/50 dark:border-slate-700/50 space-y-2">
          {Object.entries(categories).map(([key, cat]) => {
            const Icon = iconMap[cat.icon] || FileText;
            return (
              <div key={key} className="flex items-center gap-2">
                <Icon className={cn(
                  "w-3.5 h-3.5",
                  cat.explored ? "text-emerald-500" : "text-slate-400"
                )} />
                <span className="text-xs text-slate-600 dark:text-slate-400 flex-1 truncate">
                  {cat.label}
                </span>
                <div className="w-16 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className={cn(
                      "h-full transition-all",
                      cat.explored ? "bg-emerald-500" : "bg-slate-300"
                    )}
                    style={{ width: `${cat.percentage}%` }}
                  />
                </div>
                <span className="text-[10px] text-slate-500 w-8 text-right">
                  {cat.count}/{cat.limit}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UsageThermometer;
